import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { PlanosSEO } from "@/components/PlanosSEO";
import { AuthProvider } from "./contexts/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AdminRoute } from "./components/AdminRoute";
import { CustomerRoute } from "./components/CustomerRoute";
import { lazy, Suspense } from "react";
import { Skeleton } from "@/components/ui/skeleton";

// Lazy load pages for better performance
const Index = lazy(() => import("./pages/Index"));
const Login = lazy(() => import("./pages/Login"));
const Cadastro = lazy(() => import("./pages/Cadastro"));
const RecuperarSenha = lazy(() => import("./pages/RecuperarSenha"));
const RedefinirSenha = lazy(() => import("./pages/RedefinirSenha"));
const EmailConfirmado = lazy(() => import("./pages/EmailConfirmado"));

const AtivarConta = lazy(() => import("./pages/AtivarConta"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const CriarLoja = lazy(() => import("./pages/CriarLoja"));
const Produtos = lazy(() => import("./pages/Produtos"));
const Pedidos = lazy(() => import("./pages/Pedidos"));
const Cupons = lazy(() => import("./pages/Cupons"));
const Categorias = lazy(() => import("./pages/Categorias"));
const Relatorios = lazy(() => import("./pages/Relatorios"));
const Configuracoes = lazy(() => import("./pages/ConfiguracoesLoja"));
const Loja = lazy(() => import("./pages/Loja"));
const Checkout = lazy(() => import("./pages/Checkout"));
const PedidoConfirmado = lazy(() => import("./pages/PedidoConfirmado"));
const VerificarPagamento = lazy(() => import("./pages/VerificarPagamento"));
const DiagnosticoPagamentos = lazy(() => import("./pages/DiagnosticoPagamentos"));
const MeusPedidos = lazy(() => import("./pages/MeusPedidos"));
const ConsultarPedido = lazy(() => import("./pages/ConsultarPedido"));
const Acompanhamento = lazy(() => import("./pages/Acompanhamento"));
const Financeiro = lazy(() => import("./pages/Financeiro"));
const Compartilhar = lazy(() => import("./pages/Compartilhar"));
const MeuPlano = lazy(() => import("./pages/MeuPlano"));
const Clientes = lazy(() => import("./pages/Clientes"));
const ContaSeguranca = lazy(() => import("./pages/ContaSeguranca"));
const CentralAjuda = lazy(() => import("./pages/CentralAjuda"));
const ComoFunciona = lazy(() => import("./pages/ComoFunciona"));
const Comparativo = lazy(() => import("./pages/Comparativo"));
const Lojas = lazy(() => import("./pages/Lojas"));
const MarketplaceHome = lazy(() => import("./pages/MarketplaceHome"));
const Lojistas = lazy(() => import("./pages/Index"));
const Ajuda = lazy(() => import("./pages/Ajuda"));
const Contato = lazy(() => import("./pages/Contato"));
const Privacidade = lazy(() => import("./pages/Privacidade"));
const Termos = lazy(() => import("./pages/Termos"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ConfiguracoesPagamentos = lazy(() => import("./pages/ConfiguracoesPagamentos"));
const ConfiguracoesEntrega = lazy(() => import("./pages/ConfiguracoesEntrega"));
const SuccessSubscription = lazy(() => import("./pages/SuccessSubscription"));
const AdminPagamentos = lazy(() => import("./pages/AdminPagamentos"));
const AdminLayout = lazy(() => import("./components/AdminLayout"));
const OAuthConsent = lazy(() => import("./pages/OAuthConsent"));
const PagamentoPix = lazy(() => import("./pages/PagamentoPix"));

// Loading component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="space-y-4 w-full max-w-md px-4">
      <Skeleton className="h-12 w-full" />
      <Skeleton className="h-64 w-full" />
      <Skeleton className="h-12 w-full" />
    </div>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000, // 1 minute
      gcTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<MarketplaceHome />} />
              <Route path="/para-lojistas" element={<Index />} />
              <Route path="/lojistas" element={<Navigate to="/para-lojistas" replace />} />
              <Route path="/lojas" element={<Lojas />} />
              <Route path="/login" element={<Login />} />
              <Route path="/cadastro" element={<Cadastro />} />
              <Route path="/recuperar-senha" element={<RecuperarSenha />} />
              <Route path="/redefinir-senha" element={<RedefinirSenha />} />
              <Route path="/email-confirmado" element={<EmailConfirmado />} />

              <Route path="/ativar-conta" element={<AtivarConta />} />
              <Route path="/.lovable/oauth/consent" element={<OAuthConsent />} />
              <Route path="/pagamento-pix/:orderId" element={<PagamentoPix />} />
              <Route path="/pagamento/:token" element={<PagamentoPix />} />
              {/* Painel administrativo — shell único com <Outlet /> */}
              <Route element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/criar-loja" element={<CriarLoja />} />
                <Route path="/produtos" element={<Produtos />} />
                <Route path="/categorias" element={<Categorias />} />
                <Route path="/pedidos" element={<Pedidos />} />
                <Route path="/clientes" element={<Clientes />} />
                <Route path="/cupons" element={<Cupons />} />
                <Route path="/relatorios" element={<Relatorios />} />
                <Route path="/financeiro" element={<Financeiro />} />
                <Route path="/pagamentos" element={<Navigate to="/financeiro" replace />} />
                <Route path="/configuracoes-pagamentos" element={<ConfiguracoesPagamentos />} />
                <Route path="/configuracoes-entrega" element={<ConfiguracoesEntrega />} />
                <Route path="/entrega" element={<ConfiguracoesEntrega />} />
                <Route path="/configuracoes/entrega" element={<ConfiguracoesEntrega />} />
                <Route path="/compartilhar" element={<Compartilhar />} />
                <Route path="/meu-plano" element={<MeuPlano />} />
                <Route path="/success-subscription" element={<SuccessSubscription />} />
                <Route path="/conta-seguranca" element={<ContaSeguranca />} />
                <Route path="/configuracoes" element={<Configuracoes />} />
                <Route path="/configuracoes-loja" element={<Configuracoes />} />
                <Route path="/configuracoes-da-loja" element={<Configuracoes />} />
                <Route path="/configurações-loja" element={<Configuracoes />} />
                <Route path="/configurações-da-loja" element={<Configuracoes />} />
                <Route path="/configuracao-loja" element={<Configuracoes />} />
                <Route path="/configuracao-da-loja" element={<Configuracoes />} />
                <Route path="/configuracoes/loja" element={<Configuracoes />} />
                <Route path="/loja/configuracoes" element={<Configuracoes />} />
                <Route path="/configuracoes/pagamentos" element={<Navigate to="/configuracoes-pagamentos" replace />} />
                <Route path="/configuracoes/seguranca" element={<Navigate to="/conta-seguranca" replace />} />
                <Route path="/settings" element={<Configuracoes />} />
                <Route path="/store-settings" element={<Configuracoes />} />
                <Route path="/nota-fiscal" element={<Navigate to="/relatorios" replace />} />
                <Route path="/configuracoes-nfe" element={<Navigate to="/configuracoes" replace />} />
              </Route>
              <Route path="/meus-pedidos" element={<CustomerRoute><MeusPedidos /></CustomerRoute>} />
              <Route path="/consultar-pedido" element={<ConsultarPedido />} />
              <Route path="/consultar" element={<Navigate to="/consultar-pedido" replace />} />
              <Route path="/comparativo" element={<Comparativo />} />
              <Route path="/central-ajuda" element={<CentralAjuda />} />
              <Route path="/como-funciona" element={<ComoFunciona />} />
              
              <Route path="/ajuda" element={<Ajuda />} />
              <Route path="/contato" element={<Contato />} />
              <Route path="/privacidade" element={<Privacidade />} />
              <Route path="/termos" element={<Termos />} />
              <Route path="/loja/:slug" element={<Loja />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/pedido-confirmado" element={<PedidoConfirmado />} />
              <Route path="/verificar-pagamento" element={<VerificarPagamento />} />
              <Route path="/acompanhamento/:orderId" element={<Acompanhamento />} />
               <Route path="/diagnostico-pagamentos" element={<DiagnosticoPagamentos />} />
              <Route path="/planos" element={<><PlanosSEO /><Navigate to="/" replace /></>} />
              <Route path="/admin/pagamentos" element={<AdminRoute><AdminPagamentos /></AdminRoute>} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
